import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ChevronRight, Leaf, Layers, LogOut, RefreshCw, Search, Settings, Users, X } from 'lucide-react';
import type { Bed, MapMode, Plant, Zone } from '../types';
import { useAuth } from '../hooks/useAuth';
import { InviteCodeChip } from './LoginPage';
import {
  useBedMutations,
  useBeds,
  usePlantMutations,
  usePlants,
  usePropertyMap,
  useZoneMutations,
  useZones,
} from '../hooks/useGardenData';
import { api } from '../lib/api';
import { GardenMap } from '../components/map/GardenMap';
import { Sidebar } from '../components/Sidebar';
import { BedDetail } from '../components/panels/BedDetail';
import { PlantDetail } from '../components/panels/PlantDetail';
import { BedForm } from '../components/forms/BedForm';
import { PlantForm } from '../components/forms/PlantForm';
import { ZoneForm } from '../components/forms/ZoneForm';
import { BottomSheet } from '../components/BottomSheet';
import { BottomNav } from '../components/BottomNav';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { STATUS_BG, STATUS_LABELS, SUN_LABELS, TYPE_EMOJI, cn } from '../lib/utils';

type MobileTab = 'map' | 'plants' | 'beds' | 'search';
type ActiveModal = 'plant' | 'bed' | 'zone' | 'settings' | null;

export function MapPage() {
  const { logout, user, garden, members, refreshGarden } = useAuth();

  // Data
  const { data: propertyMap } = usePropertyMap();
  const { data: zones = [] } = useZones();
  const { data: beds = [] } = useBeds();
  const { data: plants = [] } = usePlants();
  const zoneMut = useZoneMutations();
  const bedMut = useBedMutations();
  const plantMut = usePlantMutations();

  // Selection state — initialized from URL so refresh/share preserves context
  const [selectedBedId, setSelectedBedId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('bed'),
  );
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('plant'),
  );

  // Map mode + drawing
  const [mapMode, setMapMode] = useState<MapMode>('view');
  const [drawingPoints, setDrawingPoints] = useState<[number, number][]>([]);

  // Pending location when in add-plant mode
  const [pendingX, setPendingX] = useState<number | null>(null);
  const [pendingY, setPendingY] = useState<number | null>(null);

  // Modals
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileTab, setMobileTab] = useState<MobileTab>('map');
  const [mobileSearch, setMobileSearch] = useState('');
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  // Settings state
  const [mapName, setMapName] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const selectedBed = useMemo(() => beds.find((b) => b.id === selectedBedId), [beds, selectedBedId]);
  const selectedPlant = useMemo(() => plants.find((p) => p.id === selectedPlantId), [plants, selectedPlantId]);
  const selectedBedForPlant = useMemo(
    () => beds.find((b) => b.id === selectedPlant?.bed_id),
    [beds, selectedPlant],
  );
  const zoneMap = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  const openModal = (m: ActiveModal) => setActiveModal(m);
  const closeModal = () => { setActiveModal(null); setPendingX(null); setPendingY(null); };

  const setMapModeAndReset = useCallback((m: MapMode) => {
    setMapMode(m);
    if (m !== 'draw-bed') setDrawingPoints([]);
  }, []);

  // ── Map interactions ─────────────────────────────────────────────────────

  const handleBedClick = useCallback((bed: Bed) => {
    if (mapMode !== 'view') return;
    setSelectedBedId(bed.id);
    setSelectedPlantId(null);
  }, [mapMode]);

  const handlePlantClick = useCallback((plant: Plant) => {
    if (mapMode !== 'view') return;
    setSelectedPlantId(plant.id);
    setSelectedBedId(null);
  }, [mapMode]);

  const handlePlantDragEnd = useCallback((plant: Plant, x: number, y: number) => {
    plantMut.move.mutate({ id: plant.id, x, y });
  }, [plantMut.move]);

  const handleMapClick = useCallback((x: number, y: number) => {
    if (mapMode === 'add-plant') {
      setPendingX(x);
      setPendingY(y);
      openModal('plant');
    }
  }, [mapMode]);

  const handleAddDrawPoint = useCallback((x: number, y: number) => {
    if (mapMode === 'draw-bed') {
      setDrawingPoints((pts) => [...pts, [x, y]]);
    }
  }, [mapMode]);

  // ── CRUD actions ─────────────────────────────────────────────────────────

  const handleCreatePlant = async (data: Partial<Plant>) => {
    const plant = await plantMut.create.mutateAsync({
      ...data,
      x: pendingX ?? data.x ?? undefined,
      y: pendingY ?? data.y ?? undefined,
    } as Partial<Plant>);
    setSelectedPlantId(plant.id);
    setSelectedBedId(null);
    closeModal();
    setMapModeAndReset('view');
  };

  const handleUpdatePlant = async (data: Partial<Plant>) => {
    if (!selectedPlantId) return;
    await plantMut.update.mutateAsync({ id: selectedPlantId, data });
  };

  const handleDeletePlant = async () => {
    if (!selectedPlantId) return;
    await plantMut.remove.mutateAsync(selectedPlantId);
    setSelectedPlantId(null);
  };

  const handleCreateBed = async (data: Partial<Bed>) => {
    const bed = await bedMut.create.mutateAsync(data);
    setSelectedBedId(bed.id);
    setSelectedPlantId(null);
    closeModal();
    setMapModeAndReset('view');
  };

  const handleUpdateBed = async (data: Partial<Bed>) => {
    if (!selectedBedId) return;
    await bedMut.update.mutateAsync({ id: selectedBedId, data });
    setMapModeAndReset('view');
  };

  const handleDeleteBed = async () => {
    if (!selectedBedId) return;
    await bedMut.remove.mutateAsync(selectedBedId);
    setSelectedBedId(null);
  };

  const handleCreateZone = async (data: Partial<Zone>) => {
    await zoneMut.create.mutateAsync(data);
    closeModal();
  };

  // ── Sync selection → URL (enables refresh-to-restore and link sharing) ───
  React.useEffect(() => {
    const params = new URLSearchParams();
    if (selectedPlantId) params.set('plant', selectedPlantId);
    else if (selectedBedId) params.set('bed', selectedBedId);
    const search = params.toString();
    window.history.replaceState(null, '', search ? `?${search}` : window.location.pathname);
  }, [selectedPlantId, selectedBedId]);

  // Handle browser back/forward
  React.useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      setSelectedPlantId(params.get('plant'));
      setSelectedBedId(params.get('bed'));
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // ── Escape key clears selection / mode ───────────────────────────────────

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mapMode !== 'view') { setMapModeAndReset('view'); return; }
        setSelectedBedId(null);
        setSelectedPlantId(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mapMode, setMapModeAndReset]);

  // Map mode instruction banner
  const modeBanner =
    mapMode === 'add-plant' ? '🌱 Click the map to place the plant' :
    mapMode === 'draw-bed'  ? `✏️  Click to add polygon vertices (${drawingPoints.length} pts)` :
    null;

  const mapData = propertyMap ?? { id: 'map-001', name: 'My Garden', image_key: null, width: 2000, height: 1500, created_at: '', updated_at: '' };

  const switchMobileTab = useCallback((tab: MobileTab) => {
    setMobileTab(tab);
    if (tab === 'search') setTimeout(() => mobileSearchRef.current?.focus(), 80);
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // ── Desktop right panel content ──────────────────────────────────────────
  const rightPanelContent = () => {
    if (selectedPlant) {
      return (
        <PlantDetail
          plant={selectedPlant}
          bed={selectedBedForPlant}
          zone={zoneMap[selectedBedForPlant?.zone_id ?? '']}
          beds={beds}
          mapMode={mapMode}
          pendingX={pendingX}
          pendingY={pendingY}
          onSetMapMode={setMapModeAndReset}
          onUpdate={handleUpdatePlant}
          onDelete={handleDeletePlant}
          onClose={() => setSelectedPlantId(null)}
        />
      );
    }
    if (selectedBed) {
      return (
        <BedDetail
          bed={selectedBed}
          zone={zoneMap[selectedBed.zone_id ?? '']}
          zones={zones}
          plants={plants}
          mapMode={mapMode}
          drawingPoints={drawingPoints}
          onSetMapMode={setMapModeAndReset}
          onClearDrawing={() => setDrawingPoints([])}
          onUpdate={handleUpdateBed}
          onDelete={handleDeleteBed}
          onPlantClick={(plant) => { setSelectedPlantId(plant.id); setSelectedBedId(null); }}
          onClose={() => setSelectedBedId(null)}
        />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 px-6 gap-3">
        <span className="text-5xl" aria-hidden="true">🗺️</span>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Click a bed or plant on the map</p>
        <p className="text-xs dark:text-gray-500">Use the sidebar to browse your garden or add new plants</p>
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-950">

      {/* ── Desktop Sidebar ─────────────────────────────────────────────── */}
      <div className="hidden md:flex w-64 shrink-0 flex-col">
        <Sidebar
          zones={zones}
          beds={beds}
          plants={plants}
          selectedBedId={selectedBedId}
          selectedPlantId={selectedPlantId}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onBedClick={handleBedClick}
          onPlantClick={handlePlantClick}
          onAddBed={() => openModal('bed')}
          onAddPlant={() => openModal('plant')}
          onAddZone={() => openModal('zone')}
          onSettingsOpen={() => { setMapName(mapData.name); openModal('settings'); }}
        />
      </div>

      {/* ── Map area ──────────────────────────────────────────────────────── */}
      <div className="flex-1 relative flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-10 shrink-0">
          <span className="text-lg" aria-hidden="true">🌿</span>
          <span className="font-serif font-bold text-garden-800 dark:text-garden-300 flex-1">GardenMap</span>
          <button onClick={() => { setMapName(mapData.name); openModal('settings'); }} aria-label="Settings" className="p-1.5 text-gray-400 dark:text-gray-500">
            <Settings size={18} />
          </button>
          <button onClick={logout} aria-label="Sign out" className="p-1.5 text-gray-400 dark:text-gray-500">
            <LogOut size={18} />
          </button>
        </div>

        {/* Mode instruction banner */}
        {modeBanner && (
          <div className="absolute top-0 md:top-2 left-1/2 -translate-x-1/2 z-20 bg-garden-700 text-white text-sm font-medium px-4 py-2 rounded-b-xl md:rounded-xl shadow-lg flex items-center gap-2">
            {modeBanner}
            <button
              onClick={() => setMapModeAndReset('view')}
              className="ml-2 hover:opacity-75 transition-opacity"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* The map */}
        <div className="flex-1 relative pb-16 md:pb-0">
          <GardenMap
            propertyMap={mapData}
            zones={zones}
            beds={beds}
            plants={plants}
            selectedBedId={selectedBedId}
            selectedPlantId={selectedPlantId}
            mapMode={mapMode}
            drawingPoints={drawingPoints}
            onBedClick={handleBedClick}
            onPlantClick={handlePlantClick}
            onPlantDragEnd={handlePlantDragEnd}
            onMapClick={handleMapClick}
            onAddDrawPoint={handleAddDrawPoint}
          />
        </div>

        {/* Mobile bottom nav */}
        <div className="md:hidden">
          <BottomNav
            active={mobileTab}
            onTab={switchMobileTab}
            onAdd={() => openModal('plant')}
          />
        </div>
      </div>

      {/* ── Desktop right panel ────────────────────────────────────────────── */}
      <div className="hidden md:flex w-80 shrink-0 flex-col border-l border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
        <div className="p-4 flex-1">{rightPanelContent()}</div>
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2 flex items-center gap-2">
          <span className="text-xs text-gray-400 flex-1 truncate">{user?.email}</span>
          <button onClick={logout} aria-label="Sign out" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1">
            <LogOut size={15} />
          </button>
        </div>
      </div>

      {/* ── Mobile bottom sheets ───────────────────────────────────────────── */}
      <div className="md:hidden">
        <BottomSheet
          open={!!selectedPlant}
          onClose={() => setSelectedPlantId(null)}
        >
          {selectedPlant && (
            <PlantDetail
              plant={selectedPlant}
              bed={selectedBedForPlant}
              zone={zoneMap[selectedBedForPlant?.zone_id ?? '']}
              beds={beds}
              mapMode={mapMode}
              pendingX={pendingX}
              pendingY={pendingY}
              onSetMapMode={setMapModeAndReset}
              onUpdate={handleUpdatePlant}
              onDelete={handleDeletePlant}
              onClose={() => setSelectedPlantId(null)}
            />
          )}
        </BottomSheet>

        <BottomSheet
          open={!!selectedBed && !selectedPlant}
          onClose={() => setSelectedBedId(null)}
        >
          {selectedBed && (
            <BedDetail
              bed={selectedBed}
              zone={zoneMap[selectedBed.zone_id ?? '']}
              zones={zones}
              plants={plants}
              mapMode={mapMode}
              drawingPoints={drawingPoints}
              onSetMapMode={setMapModeAndReset}
              onClearDrawing={() => setDrawingPoints([])}
              onUpdate={handleUpdateBed}
              onDelete={handleDeleteBed}
              onPlantClick={(plant) => { setSelectedPlantId(plant.id); setSelectedBedId(null); }}
              onClose={() => setSelectedBedId(null)}
            />
          )}
        </BottomSheet>
      </div>

      {/* ── Mobile tab panels (Plants / Beds / Search) ────────────────────── */}
      {mobileTab !== 'map' && (
        <div className="md:hidden fixed inset-x-0 z-[850] bg-white dark:bg-gray-900 overflow-y-auto" style={{ top: 48, bottom: 64 }}>

          {/* Plants panel */}
          {mobileTab === 'plants' && (
            <div className="flex flex-col h-full">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
                <h2 className="font-serif font-semibold text-garden-800 dark:text-garden-300">All Plants</h2>
                <p className="text-xs text-gray-400">{plants.length} plant{plants.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800">
                {plants.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
                    <Leaf size={32} />
                    <p className="text-sm">No plants yet — tap + to add one</p>
                  </div>
                ) : (
                  plants
                    .slice()
                    .sort((a, b) => a.common_name.localeCompare(b.common_name))
                    .map((plant) => {
                      const bed = beds.find((b) => b.id === plant.bed_id);
                      return (
                        <button
                          key={plant.id}
                          onClick={() => {
                            setSelectedPlantId(plant.id);
                            setSelectedBedId(null);
                            setMobileTab('map');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                        >
                          <span className="text-2xl" aria-hidden="true">{TYPE_EMOJI[plant.plant_type]}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{plant.common_name}</p>
                            {plant.cultivar && <p className="text-xs text-gray-400 italic truncate">'{plant.cultivar}'</p>}
                            {bed && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{bed.name}</p>}
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge className={STATUS_BG[plant.status]}>{STATUS_LABELS[plant.status]}</Badge>
                          </div>
                          <ChevronRight size={16} className="text-gray-300 shrink-0" />
                        </button>
                      );
                    })
                )}
              </div>
            </div>
          )}

          {/* Beds panel */}
          {mobileTab === 'beds' && (
            <div className="flex flex-col h-full">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
                <h2 className="font-serif font-semibold text-garden-800 dark:text-garden-300">Zones & Beds</h2>
                <p className="text-xs text-gray-400">{zones.length} zone{zones.length !== 1 ? 's' : ''}, {beds.length} bed{beds.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex-1 overflow-y-auto py-2">
                {zones.map((zone) => {
                  const zoneBeds = beds.filter((b) => b.zone_id === zone.id);
                  return (
                    <div key={zone.id} className="mb-1">
                      {/* Zone header */}
                      <div className="flex items-center gap-2 px-4 py-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: zone.color }} />
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{zone.name}</span>
                      </div>
                      {zoneBeds.map((bed) => {
                        const bedPlants = plants.filter((p) => p.bed_id === bed.id);
                        return (
                          <button
                            key={bed.id}
                            onClick={() => {
                              setSelectedBedId(bed.id);
                              setSelectedPlantId(null);
                              setMobileTab('map');
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 border-t border-gray-50 dark:border-gray-800 transition-colors"
                          >
                            <Layers size={18} className="text-gray-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{bed.name}</p>
                              {bed.sun_exposure && (
                                <p className="text-xs text-gray-400">{SUN_LABELS[bed.sun_exposure]}</p>
                              )}
                            </div>
                            <span className="text-xs text-gray-400 shrink-0">{bedPlants.length} plant{bedPlants.length !== 1 ? 's' : ''}</span>
                            <ChevronRight size={16} className="text-gray-300 shrink-0" />
                          </button>
                        );
                      })}
                      {zoneBeds.length === 0 && (
                        <p className="text-xs text-gray-400 px-8 py-2 italic">No beds in this zone</p>
                      )}
                    </div>
                  );
                })}
                {/* Unzoned beds */}
                {beds.filter((b) => !b.zone_id).map((bed) => {
                  const bedPlants = plants.filter((p) => p.bed_id === bed.id);
                  return (
                    <button
                      key={bed.id}
                      onClick={() => {
                        setSelectedBedId(bed.id);
                        setSelectedPlantId(null);
                        setMobileTab('map');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 border-t border-gray-50 transition-colors"
                    >
                      <Layers size={18} className="text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{bed.name}</p>
                        <p className="text-xs text-gray-400 italic">No zone</p>
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">{bedPlants.length} plants</span>
                      <ChevronRight size={16} className="text-gray-300 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search panel */}
          {mobileTab === 'search' && (
            <div className="flex flex-col h-full">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={mobileSearchRef}
                    type="search"
                    value={mobileSearch}
                    onChange={(e) => setMobileSearch(e.target.value)}
                    placeholder="Search plants by name, cultivar…"
                    aria-label="Search plants"
                    className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-xl focus:outline-none focus:border-garden-400 focus:bg-white dark:focus:bg-gray-800 transition"
                  />
                  {mobileSearch && (
                    <button
                      onClick={() => setMobileSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800">
                {mobileSearch.trim() === '' ? (
                  <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
                    <Search size={32} />
                    <p className="text-sm">Start typing to search</p>
                  </div>
                ) : (() => {
                  const q = mobileSearch.toLowerCase();
                  const results = plants.filter(
                    (p) =>
                      p.common_name.toLowerCase().includes(q) ||
                      p.botanical_name?.toLowerCase().includes(q) ||
                      p.cultivar?.toLowerCase().includes(q) ||
                      p.notes?.toLowerCase().includes(q),
                  );
                  if (results.length === 0) {
                    return (
                      <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
                        <p className="text-sm">No plants match "{mobileSearch}"</p>
                      </div>
                    );
                  }
                  return results.map((plant) => {
                    const bed = beds.find((b) => b.id === plant.bed_id);
                    return (
                      <button
                        key={plant.id}
                        onClick={() => {
                          setSelectedPlantId(plant.id);
                          setSelectedBedId(null);
                          setMobileSearch('');
                          setMobileTab('map');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                      >
                        <span className="text-2xl" aria-hidden="true">{TYPE_EMOJI[plant.plant_type]}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{plant.common_name}</p>
                          {plant.botanical_name && <p className="text-xs text-gray-400 italic truncate">{plant.botanical_name}</p>}
                          {bed && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{bed.name}</p>}
                        </div>
                        <Badge className={cn(STATUS_BG[plant.status], 'shrink-0')}>{STATUS_LABELS[plant.status]}</Badge>
                        <ChevronRight size={16} className="text-gray-300 shrink-0" />
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Add Plant modal ───────────────────────────────────────────────── */}
      <Modal open={activeModal === 'plant'} onClose={closeModal} title="Add Plant" size="lg">
        <PlantForm
          beds={beds}
          pendingX={pendingX}
          pendingY={pendingY}
          mapMode={mapMode}
          onSetMapMode={setMapModeAndReset}
          onSubmit={handleCreatePlant}
          onCancel={closeModal}
        />
      </Modal>

      {/* ── Add Bed modal ─────────────────────────────────────────────────── */}
      <Modal open={activeModal === 'bed'} onClose={closeModal} title="Add Garden Bed" size="lg">
        <BedForm
          zones={zones}
          mapMode={mapMode}
          drawingPoints={drawingPoints}
          onSetMapMode={setMapModeAndReset}
          onClearDrawing={() => setDrawingPoints([])}
          onSubmit={handleCreateBed}
          onCancel={closeModal}
        />
      </Modal>

      {/* ── Add Zone modal ────────────────────────────────────────────────── */}
      <Modal open={activeModal === 'zone'} onClose={closeModal} title="Add Zone" size="sm">
        <ZoneForm
          onSubmit={handleCreateZone}
          onCancel={closeModal}
        />
      </Modal>

      {/* ── Settings modal ────────────────────────────────────────────────── */}
      <Modal open={activeModal === 'settings'} onClose={closeModal} title="Settings" size="md">
        <div className="flex flex-col gap-5">

          {/* Map settings */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Map Settings</h3>
            <div className="flex flex-col gap-3">
              <Input
                label="Map Name"
                value={mapName}
                onChange={(e) => setMapName(e.target.value)}
                placeholder="My Garden"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Replace Base Map Image</label>
                <input
                  type="file"
                  accept="image/*"
                  aria-label="Upload base map image"
                  className="text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-garden-50 file:text-garden-700 hover:file:bg-garden-100"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setSavingSettings(true);
                    await api.uploadMapImage(mapData.id, file);
                    setSavingSettings(false);
                  }}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload a top-down yard illustration (PNG/JPG/SVG)</p>
              </div>
              <Button
                onClick={async () => {
                  setSavingSettings(true);
                  await api.updateMap(mapData.id, { name: mapName });
                  setSavingSettings(false);
                  closeModal();
                }}
                loading={savingSettings}
              >
                Save Settings
              </Button>
            </div>
          </div>

          {/* Garden / sharing */}
          {garden && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
                <Users size={14} aria-hidden="true" /> Garden &amp; Sharing
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Share this code so others can join <strong className="text-gray-700 dark:text-gray-300">{garden.name}</strong>
              </p>
              <div className="flex items-center gap-2 mb-3">
                <InviteCodeChip code={garden.invite_code} />
                <button
                  type="button"
                  title="Generate new code"
                  aria-label="Generate new invite code"
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={async () => {
                    await api.regenerateInvite();
                    await refreshGarden();
                  }}
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              {members.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    {members.length} member{members.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex flex-col gap-1">
                    {members.map((m) => (
                      <div key={m.id} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-garden-100 dark:bg-garden-900/40 flex items-center justify-center text-xs font-semibold text-garden-700 dark:text-garden-300 shrink-0">
                          {m.email[0].toUpperCase()}
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                          {m.email}
                          {m.id === user?.id && <span className="text-xs text-gray-400 ml-1">(you)</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Account</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{user?.email}</p>
            <Button variant="danger" size="sm" onClick={() => { closeModal(); logout(); }}>
              <LogOut size={14} /> Sign out
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
